import { useMemo } from 'react';
import axios from 'axios';
import { useQueries } from 'react-query';

import { IRangeTrace } from '../stores/useSeismicStore';
import { TraceHeader } from '../models/types/TraceHeader';
import { SurveyType } from '../models/enums/SurveyType';
import { ISurveyMetadata } from '../models/interfaces/ISurveyMetadata';
import { LineType } from '../models/enums/LineType';
import { IResponseAPI } from 'models/interfaces/IResponseAPI';
import { useSessionStore } from 'session/useSessionStore';

type TraceSlice = {
    minTrace: number;
    maxTrace: number;
};

export type UseTraceHeadersForRangeParams = {
    range: IRangeTrace;
    surveyMetadata: ISurveyMetadata | null;
    increment: number;
    minTrace: number;
    maxTrace: number;
    queryRange: number;
    isInverted: boolean;
    lineNumber: number;
    lineType: LineType;
    sortOption: string;
    showSortOption: boolean;
};

export function buildTraceSlices(
    range: IRangeTrace,
    increment: number,
    queryRange: number,
    isInverted: boolean,
    minTrace: number,
    maxTrace: number,
): TraceSlice[] {
    const slices: TraceSlice[] = [];
    const effective: IRangeTrace = isInverted
        ? { initial: maxTrace - range.end + minTrace, end: maxTrace - range.initial + minTrace }
        : range;

    for (let i = effective.initial; i <= effective.end; i += (queryRange * increment) + 1) {
        const sliceEnd = Math.min(i + queryRange * increment, effective.end - 1);
        slices.push({ minTrace: i, maxTrace: sliceEnd });
    }
    return slices;
}

export function useTraceHeadersForRange(params: UseTraceHeadersForRangeParams) {
    const [host, jwt] = useSessionStore(state => [
        state.tenantConfiguration?.endpoints.traceHeader,
        state.jwtToken,
    ]);

    const samplesBytePerTrace = params.surveyMetadata
        ? params.surveyMetadata.Header.SamplesPerTrace * params.surveyMetadata.Header.ByteSize
        : 0;
    const volumePath = params.surveyMetadata?.VolumePath;
    const surveyType = params.surveyMetadata?.Type;

    const slices: TraceSlice[] = useMemo(() => {
        if (!params.surveyMetadata || params.range.end === 0) {
            return [];
        }
        return buildTraceSlices(
            params.range,
            params.increment,
            params.queryRange,
            params.isInverted,
            params.minTrace,
            params.maxTrace,
        );
    }, [
        params.range,
        params.surveyMetadata,
        params.increment,
        params.queryRange,
        params.isInverted,
        params.minTrace,
        params.maxTrace,
    ]);

    const queries = useQueries(slices.map(slice => ({
        queryKey: [
            'TraceHeadersSlice',
            volumePath,
            surveyType,
            slice.minTrace,
            slice.maxTrace,
            params.lineNumber,
            params.lineType,
            params.showSortOption ? params.sortOption : null,
        ],
        queryFn: async (): Promise<TraceHeader[]> => {
            if (surveyType === SurveyType.Seismic2D) {
                const response = await axios.get<IResponseAPI<TraceHeader[]>>(
                    host + '/Seismic/SeismicTraces/GetTracesHeaders',
                    {
                        params: {
                            minTrace: slice.minTrace,
                            maxTrace: slice.maxTrace,
                            samplesBytePerTrace,
                            path: volumePath,
                            sortOption: params.showSortOption ? params.sortOption : null,
                        },
                        headers: {
                            Authorization: 'Bearer ' + jwt,
                        },
                    },
                );
                return response.data.Result;
            }
            const response = await axios.get<IResponseAPI<TraceHeader[]>>(
                host + '/Seismic/SeismicTraces/GetTracesHeaders3D',
                {
                    params: {
                        minTrace: slice.minTrace,
                        maxTrace: slice.maxTrace,
                        path: volumePath,
                        lineType: params.lineType,
                        lineNumber: params.lineNumber,
                    },
                    headers: {
                        Authorization: 'Bearer ' + jwt,
                    },
                },
            );
            return response.data.Result;
        },
        enabled: !!host && !!jwt && !!volumePath && surveyType !== undefined,
        staleTime: Infinity,
        cacheTime: 1000 * 60 * 10,
        retry: 3,
    })));

    const isLoading = queries.length > 0 && queries.some(query => query.isLoading);
    const isFetching = queries.some(query => query.isFetching);

    const traceHeaders: TraceHeader[] = useMemo(() => {
        const merged: TraceHeader[] = [];
        const seen = new Set<number>();
        for (const query of queries) {
            if (!query.data) {
                continue;
            }
            for (const header of query.data) {
                if (!seen.has(header.TraceNumber)) {
                    seen.add(header.TraceNumber);
                    merged.push(header);
                }
            }
        }
        merged.sort((a, b) => a.TraceNumber - b.TraceNumber);
        return merged;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queries.map(query => query.dataUpdatedAt).join('-')]);

    return { traceHeaders, isLoading, isFetching };
}
